import { Box, Modal, IconButton, useMediaQuery } from "@material-ui/core";
import { useMutation } from "@apollo/react-hooks";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { StyledGrid, StyledP, Button } from "components/simple";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { breakpoints, color, fontSize, spacingDefaults } from "style/constants";
import { showSuccessNotification } from "state";
import { ReactComponent as CloseButton } from "assets/close.svg";
import ImagingSessionImage from "assets/mrimachinebluebg.svg";
import { UpcomingSession } from "..";
import { CancelImagingSession } from "graphql/sessions/sessions.gql";
import { CycleSummaryQuery } from "graphql/user/user.gql";

interface UpcomingSessionProps {
  session: UpcomingSession;
  index: number;
}

const UpcomingImagingSession: React.FC<UpcomingSessionProps> = ({
  session,
  index
}: UpcomingSessionProps) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);

  const mobile = useMediaQuery(`(max-width:${breakpoints.lg}px)`);
  const date = format(new Date(session.sessionTime), "MMMM d, yyyy");
  const time = format(new Date(session.sessionTime), "p");
  const timezoneAbbreviation = new Date()
    .toLocaleTimeString("en-us", { timeZoneName: "short" })
    .split(" ")[2];

  const [cancelImagingSession] = useMutation(CancelImagingSession, {
    variables: {
      input: { acuityConfirmationNumber: session.acuityConfirmationNumber }
    },
    onCompleted: () => {
      dispatch(
        showSuccessNotification(
          "Success",
          "Successfully cancelled imaging appointment"
        )
      );
    },
    refetchQueries: [{ query: CycleSummaryQuery }]
  });

  const message = `You have an imaging session scheduled at ${time} ${timezoneAbbreviation} on ${date}.  Please ensure your assessments are completed by that time.`;

  return (
    <StyledGrid
      item
      key={index}
      lg={6}
      paddingBottom={spacingDefaults.normal}
      paddingRight={spacingDefaults.normal}
      xs={12}
    >
      <StyledGrid
        alignItems="center"
        backgroundColor={color.WHITE}
        border={`1px solid ${color.MUTEDGRAY}`}
        borderRadius="8px"
        container
        height="100%"
        justifyContent="center"
        key={index}
        marginTop={spacingDefaults.xsmall}
        padding={spacingDefaults.large}
      >
        <StyledGrid item md={4}>
          <img
            alt="ImagingSessionImage"
            src={ImagingSessionImage}
            style={{ maxHeight: "200px" }}
            width="100%"
          />
        </StyledGrid>
        <StyledGrid item md={8} paddingLeft={spacingDefaults.normal}>
          <Box
            color={color.GRAPHITE}
            fontSize={fontSize.normal}
            component="p"
            margin="0"
          >
            {message}
          </Box>

          {session.acuityConfirmationNumber && (
            <StyledGrid
              container
              spacing={1}
              marginTop={spacingDefaults.normal}
            >
              <StyledGrid item md={6} xs={12}>
                <Button
                  aria-label="cancel-session"
                  longText
                  fullWidth
                  inverted
                  onClick={() => setShowModal(true)}
                >
                  Cancel
                </Button>
              </StyledGrid>
              <StyledGrid item md={6} xs={12}>
                <Link
                  to={`/scheduling/${session.type}?reschedule=${session.acuityConfirmationNumber}`}
                >
                  <Button
                    aria-label="reschedule-session"
                    longText
                    fullWidth
                    inverted
                  >
                    Reschedule
                  </Button>
                </Link>
              </StyledGrid>
              <Modal open={showModal}>
                <>
                  <StyledGrid
                    container
                    absoluteCenter
                    backgroundColor={color.WHITE}
                    borderRadius="8px"
                    direction="column"
                    maxWidth="500px"
                    mobileMaxWidth="98%"
                    padding={spacingDefaults.normal}
                  >
                    <StyledGrid item alignSelf="flex-end" position="fixed">
                      <IconButton
                        aria-label="close-modal"
                        onClick={() => setShowModal(false)}
                      >
                        <CloseButton
                          strokeWidth="1px"
                          height="15px"
                          width="15px"
                        />
                      </IconButton>
                    </StyledGrid>
                    <StyledGrid container item height="100%" width="100%">
                      <StyledGrid item width="100%" textAlign="center">
                        <StyledP
                          textAlign="center"
                          fontSize={fontSize.large}
                          fontWeight="500"
                          marginbottom={spacingDefaults.normal}
                        >
                          Cancel {session.type} session
                        </StyledP>
                      </StyledGrid>
                      <StyledGrid
                        item
                        width="100%"
                        textAlign="center"
                        marginBottom={spacingDefaults.normal}
                      >
                        <StyledGrid paddingBottom="16px">
                          <StyledP width="100%" textAlign="center">
                            Are you sure you want to cancel this {session.type}{" "}
                            session?
                          </StyledP>
                        </StyledGrid>
                      </StyledGrid>
                      <StyledGrid item width="100%" textAlign="center">
                        <Button
                          aria-label="cancel-session"
                          fullWidth={mobile}
                          onClick={() => {
                            cancelImagingSession(), setShowModal(false);
                          }}
                        >
                          Cancel Session
                        </Button>
                      </StyledGrid>
                    </StyledGrid>
                  </StyledGrid>
                </>
              </Modal>
            </StyledGrid>
          )}
        </StyledGrid>
      </StyledGrid>
    </StyledGrid>
  );
};

export default UpcomingImagingSession;
